import React, { useEffect, useState } from "react";
import "../css/ExpenseModal.css";
import { getISTDateInputValue } from "../utils/dateTime";

const API_URL = "/v1/expenses";

const getEmptyForm = () => ({
    clinicId: 1,
    expenseType: "BILL",
    description: "",
    amount: "",
    expenseDate: getISTDateInputValue(),
    vendorName: "",
    invoiceNumber: "",
    paymentMode: "UPI",
    notes: "",
    createdBy: "admin"
});

const ExpenseModal = ({ isOpen, onClose, clinicId }) => {


    const expenseTypes = [
        { value: "BILL", label: "Bill" },
        { value: "ITEM_PURCHASE", label: "Item Purchase" },
        { value: "FROM_OTHER_CLINIC", label: "Item From Other Clinic" },
        { value: "FROM_OWN_CLINIC", label: "Item From Own Clinic" },
        { value: "RENT", label: "Rent" },
        { value: "SALARY", label: "Salary" },
        { value: "ELECTRICITY", label: "Electricity" },
        { value: "INTERNET", label: "Internet" },
        { value: "MEDICINE", label: "Medicine" },
        { value: "MEDICAL_SUPPLIES", label: "Medical Supplies" },
        { value: "MAINTENANCE", label: "Maintenance" },
        { value: "OTHER", label: "Other" }
    ];

    const [formData, setFormData] = useState(getEmptyForm());
    const [editingExpense, setEditingExpense] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [message, setMessage] = useState("");

    const isEditing = editingExpense !== null;

    const loadExpenses = async () => {

        setLoadingExpenses(true);

        try {

            const response = await fetch(
                `${API_URL}?clinicId=${clinicId}`
            );

            if (!response.ok) {
                throw new Error("Failed to load expenses");
            }

            const data = await response.json();

            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.content)
                    ? data.content
                    : Array.isArray(data?.data)
                        ? data.data
                        : Array.isArray(data?.expenses)
                            ? data.expenses
                            : [];

            setExpenses(list);

        } catch (error) {

            console.error("Unable to load expenses:", error);

            setMessage("Unable to load expenses.");

        } finally {

            setLoadingExpenses(false);
        }
    };

    useEffect(() => {

        if (isOpen) {

            loadExpenses();

            setMessage("");
            setEditingExpense(null);
            setFormData(getEmptyForm());
        }

    }, [isOpen]);

    const handleChange = (event) => {

        const { name, value } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value
        }));
    };

    const handleEdit = (expense) => {

        setEditingExpense(expense);

        setFormData({
            clinicId: expense.clinicId || 1,
            expenseType: expense.expenseType || "BILL",
            description: expense.description || "",
            amount: expense.amount ?? "",
            expenseDate: expense.expenseDate || "",
            vendorName: expense.vendorName || "",
            invoiceNumber: expense.invoiceNumber || "",
            paymentMode: expense.paymentMode || "UPI",
            notes: expense.notes || "",
            createdBy: expense.createdBy || "admin"
        });

        setMessage("");
    };

    const handleCancelEdit = () => {

        setEditingExpense(null);
        setFormData(getEmptyForm());
        setMessage("");
    };

    const handleDelete = async (id) => {

        if (!window.confirm(
            "Are you sure you want to delete this expense?"
        )) {
            return;
        }

        setLoading(true);
        setMessage("");

        try {

            const response = await fetch(
                `${API_URL}/${id}?clinicId=${clinicId}`,
                {
                    method: "DELETE"
                }
            );

            if (!response.ok) {
                throw new Error("Failed to delete expense");
            }

            if (
                editingExpense &&
                editingExpense.id === id
            ) {
                setEditingExpense(null);
                setFormData(getEmptyForm());
            }

            setMessage("Expense deleted successfully.");

            await loadExpenses();

        } catch (error) {

            console.error("Delete expense error:", error);

            setMessage(
                "Unable to delete expense. Please try again."
            );

        } finally {

            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {

        event.preventDefault();

        setLoading(true);
        setMessage("");

        try {

            const requestBody = {
                clinicId: clinicId,
                expenseType: formData.expenseType,
                description: formData.description,
                amount: parseFloat(formData.amount),
                expenseDate: formData.expenseDate,
                vendorName: formData.vendorName,
                invoiceNumber: formData.invoiceNumber,
                paymentMode: formData.paymentMode,
                notes: formData.notes,
                createdBy: formData.createdBy
            };

            const url = isEditing
                ? `${API_URL}/${editingExpense.id}?clinicId=${clinicId}`
                : API_URL;

            const method = isEditing
                ? "PUT"
                : "POST";

            const response = await fetch(
                url,
                {
                    method: method,
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            if (!response.ok) {
                throw new Error(
                    isEditing
                        ? "Failed to update expense"
                        : "Failed to save expense"
                );
            }

            const result = await response.json();

            console.log(
                isEditing
                    ? "Expense updated:"
                    : "Expense created:",
                result
            );

            setMessage(
                isEditing
                    ? "Expense updated successfully."
                    : "Expense saved successfully."
            );

            setEditingExpense(null);
            setFormData(getEmptyForm());

            await loadExpenses();

        } catch (error) {

            console.error(error);

            setMessage(
                isEditing
                    ? "Unable to update expense. Please try again."
                    : "Unable to save expense. Please try again."
            );

        } finally {

            setLoading(false);
        }
    };

    const formatAmount = (amount) => {

        if (amount === null || amount === undefined) {
            return "₹0.00";
        }

        return `₹${Number(amount).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        )}`;
    };

    const formatExpenseType = (type) => {

        if (!type) {
            return "-";
        }

        return type
            .replaceAll("_", " ")
            .toLowerCase()
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="expense-modal-overlay">

            <div className="expense-modal">

                <div className="expense-modal-header">

                    <h2>
                        {isEditing
                            ? "Edit Expense"
                            : "Add Expense"}
                    </h2>

                    <button
                        type="button"
                        className="expense-close-button"
                        onClick={onClose}
                    >
                        ×
                    </button>

                </div>

                <form onSubmit={handleSubmit}>

                    <div className="expense-form-grid">

                        <div className="expense-form-group">

                            <label>
                                Expense Type
                            </label>

                            <select
                                name="expenseType"
                                value={formData.expenseType}
                                onChange={handleChange}
                                required
                            >
                                {expenseTypes.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>

                        </div>

                        <div className="expense-form-group">

                            <label>
                                Expense Date
                            </label>

                            <input
                                type="date"
                                name="expenseDate"
                                value={formData.expenseDate}
                                onChange={handleChange}
                                required
                            />

                        </div>

                        <div className="expense-form-group full-width">

                            <label>
                                Description
                            </label>

                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="e.g. Electricity Bill"
                                required
                            />

                        </div>

                        <div className="expense-form-group">

                            <label>
                                Amount
                            </label>

                            <input
                                type="number"
                                name="amount"
                                value={formData.amount}
                                onChange={handleChange}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                required
                            />

                        </div>

                        <div className="expense-form-group">

                            <label>
                                Vendor / Clinic
                            </label>

                            <input
                                type="text"
                                name="vendorName"
                                value={formData.vendorName}
                                onChange={handleChange}
                                placeholder="e.g. BESCOM"
                            />

                        </div>

                        <div className="expense-form-group">

                            <label>
                                Invoice Number
                            </label>

                            <input
                                type="text"
                                name="invoiceNumber"
                                value={formData.invoiceNumber}
                                onChange={handleChange}
                                placeholder="e.g. ELEC-001"
                            />

                        </div>

                        <div className="expense-form-group">

                            <label>
                                Payment Mode
                            </label>

                            <select
                                name="paymentMode"
                                value={formData.paymentMode}
                                onChange={handleChange}
                            >
                                <option value="UPI">UPI</option>
                                <option value="CASH">Cash</option>
                                <option value="CARD">Card</option>
                                <option value="BANK_TRANSFER">
                                    Bank Transfer
                                </option>
                                <option value="CHEQUE">Cheque</option>
                                <option value="OTHER">Other</option>
                            </select>

                        </div>

                        <div className="expense-form-group full-width">

                            <label>
                                Notes
                            </label>

                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder="Additional notes..."
                                rows="3"
                            />

                        </div>

                    </div>

                    {message && (
                        <div className="expense-message">
                            {message}
                        </div>
                    )}

                    <div className="expense-modal-footer">

                        {isEditing && (
                            <button
                                type="button"
                                className="expense-cancel-button"
                                onClick={handleCancelEdit}
                                disabled={loading}
                            >
                                Cancel Edit
                            </button>
                        )}

                        {!isEditing && (
                            <button
                                type="button"
                                className="expense-cancel-button"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                        )}

                        <button
                            type="submit"
                            className="expense-save-button"
                            disabled={loading}
                        >
                            {loading
                                ? isEditing
                                    ? "Updating..."
                                    : "Saving..."
                                : isEditing
                                    ? "Update Expense"
                                    : "Save Expense"
                            }
                        </button>

                    </div>

                </form>

                <div className="expense-list-section">

                    <div className="expense-list-header">

                        <h3>
                            Existing Expenses
                        </h3>

                        <button
                            type="button"
                            className="expense-refresh-button"
                            onClick={loadExpenses}
                            disabled={loadingExpenses}
                        >
                            {loadingExpenses
                                ? "Loading..."
                                : "Refresh"}
                        </button>

                    </div>

                    {loadingExpenses ? (

                        <div className="expense-list-message">
                            Loading expenses...
                        </div>

                    ) : expenses.length === 0 ? (

                        <div className="expense-list-message">
                            No expenses found.
                        </div>

                    ) : (

                        <div className="expense-table-container">

                            <table className="expense-table">

                                <thead>

                                    <tr>
                                        <th>Date</th>
                                        <th>Vendor</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {expenses.map((expense) => (

                                        <tr key={expense.id}>

                                            <td>
                                                {expense.expenseDate || "-"}
                                            </td>
                                            <td>
                                                {formatExpenseType(
                                                    expense.vendorName
                                                )}
                                            </td>
                                            <td>
                                                {formatExpenseType(
                                                    expense.expenseType
                                                )}
                                            </td>

                                            <td>
                                                {expense.description || "-"}
                                            </td>

                                            <td className="expense-amount">
                                                {formatAmount(
                                                    expense.amount
                                                )}
                                            </td>

                                            <td>

                                                <div className="expense-actions">

                                                    <button
                                                        type="button"
                                                        className="expense-edit-button"
                                                        onClick={() =>
                                                            handleEdit(expense)
                                                        }
                                                        disabled={loading}
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="expense-delete-button"
                                                        onClick={() =>
                                                            handleDelete(
                                                                expense.id
                                                            )
                                                        }
                                                        disabled={loading}
                                                    >
                                                        Delete
                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    ))}

                                </tbody>

                            </table>

                        </div>

                    )}

                </div>

            </div>

        </div>
    );


};

export default ExpenseModal;